import { PlusIcon } from "@images/svgIcons/plusIcon"
import { word } from "@modules/core/utils/i18n"
import {
  SearchMultipleUserInputModal,
  SearchMultipleUserInputModalRef,
} from "@modules/formik/components/searchMultipleUserInput"
import { User } from "@modules/profile/profileTypes"
import { ProgramFormikType, ProgramInfiniteInputKey } from "@modules/programs/programFormikType"
import { ProgramOnOffFormikType, ProgramOnOffInputKey } from "@modules/programs/programOnOffFormikType"
import { SharedCoach, SharedCoachStatus } from "@modules/programs/programTypes"
import { SharedCoachView } from "@modules/sharedCoach/components/sharedCoachView"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { FieldValueChange } from "@src/typings/formik"
import { FieldInputProps } from "formik"
import { useCallback, useRef } from "react"
import { useTheme } from "styled-components"

type SharedCoachPopUpProps = {
  setFieldValue: FieldValueChange<ProgramFormikType | ProgramOnOffFormikType>
  getFieldProps: (key: ProgramInfiniteInputKey | ProgramOnOffInputKey) => FieldInputProps<SharedCoach[] | undefined>
}

export const ProgramSharedCoachForm = ({ setFieldValue, getFieldProps }: SharedCoachPopUpProps) => {
  const confirmPopup = useConfirmPopup()
  const theme = useTheme()
  const searchMultipleUserInputModalRef = useRef<SearchMultipleUserInputModalRef>(null)

  const removeSharedCoach = useCallback(
    async (profile: User) => {
      await setFieldValue(
        "sharedCoaches",
        [...(getFieldProps("sharedCoaches").value?.filter((coach: SharedCoach) => coach.user.id !== profile.id) ?? [])],
        false,
      )
    },
    [getFieldProps, setFieldValue],
  )

  const removeSharedCoachDialog = useCallback(
    (profile: User) => {
      confirmPopup.show({
        title: word("program.sharedCoach.form.remove"),
        message: (
          <div className="flex flex-column gap-4">
            <HUText fontStyle="BM" color={theme.neutralColor700} className="m-auto">
              {word("program.sharedCoach.form.removeDetails")}
            </HUText>
            <SharedCoachView profile={profile} />
          </div>
        ),
        accept: () => removeSharedCoach(profile),
        footerProps: {
          confirmText: word("global.delete"),
          confirmClassName: "flex-1",
          cancelClassName: "flex-1",
          footerClassName: "flex flex-row justify-content-around w-full",
        },
        width: "418px",
      })
    },
    [confirmPopup, removeSharedCoach, theme.neutralColor700],
  )

  const onConfirmAddSharedCoachs = useCallback(
    async (selectedMembers: User[]) => {
      await setFieldValue(
        "sharedCoaches",
        [
          ...(getFieldProps("sharedCoaches").value ?? []),
          ...selectedMembers.map((profile) => ({
            user: profile,
            role: "STANDARD",
            status: SharedCoachStatus.PENDING,
          })),
        ],
        false,
      )
    },
    [getFieldProps, setFieldValue],
  )

  return (
    <div className="flex flex-column gap-2">
      <HUText type="label" color={theme.neutralColor700} fontStyle="LS" style={{ marginBottom: 7 }}>
        {word("program.sharedCoach.title").toUpperCase()}
      </HUText>

      <div style={{ gap: 12, display: "flex", flexDirection: "column" }}>
        <HUButton
          type="Right_Icon"
          colorType="Quaternary"
          text={word("program.sharedCoach.form.add")}
          onClick={() => searchMultipleUserInputModalRef.current?.open()}
          className="w-full"
          icon={{ iconView: <PlusIcon color={theme.mainBlack} />, iconSize: 14 }}
          size="S"
        />

        {getFieldProps("sharedCoaches").value?.map((coach: SharedCoach) => (
          <SharedCoachView
            key={coach.user.id}
            profile={coach.user}
            status={coach.status}
            isAuthor
            isEditing
            onRemove={() => removeSharedCoachDialog(coach.user)}
          />
        ))}
        <HUText fontStyle="BS" color={theme.neutralColor600}>
          {word("program.sharedCoach.form.description")}
        </HUText>
      </div>

      <SearchMultipleUserInputModal
        title={word("program.sharedCoach.form.add")}
        ref={searchMultipleUserInputModalRef}
        values={getFieldProps("sharedCoaches").value?.map((item) => item.user.id) ?? []}
        emptyDescription={word("program.sharedCoach.emptyView.description")}
        searchPlaceholder={word("program.sharedCoach.form.search")}
        onConfirm={onConfirmAddSharedCoachs}
      />
    </div>
  )
}
